import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogIn, LogOut, RefreshCw, Loader2, Inbox, Sparkles, Mail } from "lucide-react";
import { API_URL } from "../data/site";
import { AdminRecapForm } from "../components/AdminRecapForm";
import { AdminQuotes } from "../components/AdminQuotes";
import { AdminPayments } from "../components/AdminPayments";
import { Reveal } from "../components/Reveal";

const TABS = [
  { id: "inquiries", label: "Inquiries" },
  { id: "quotes", label: "Quotes" },
  { id: "payments", label: "Payments" },
];

export default function Admin() {
  const location = useLocation();
  const [tab, setTab] = useState("inquiries");
  const [auth, setAuth] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [drafts, setDrafts] = useState({});

  const draftReply = async (q) => {
    setDrafts((d) => ({ ...d, [q.id]: { text: "", loading: true } }));
    try {
      const res = await fetch(`${API_URL}/inquiries/${q.id}/draft-reply`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok || !res.body) throw new Error("draft failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const snapshot = acc;
        setDrafts((d) => ({ ...d, [q.id]: { text: snapshot, loading: true } }));
      }
      setDrafts((d) => ({ ...d, [q.id]: { text: acc, loading: false } }));
    } catch {
      setDrafts((d) => ({ ...d, [q.id]: undefined }));
      toast.error("Could not draft a reply right now.");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const check = async () => {
      if (token) {
        // Arrived from an emailed sign-in link: exchange the one-time token for a session.
        window.history.replaceState({}, "", "/admin");
        try {
          const res = await axios.post(`${API_URL}/auth/verify`, { token }, { withCredentials: true });
          setAuth({ user: res.data });
          return;
        } catch (err) {
          const detail = err.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : "Sign-in failed. Please request a new link.");
        }
      }
      try {
        const res = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
        setAuth({ user: res.data });
      } catch {
        setAuth(false);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInquiries = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API_URL}/inquiries`, { withCredentials: true });
      setInquiries(res.data);
      setForbidden(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
      } else if (err.response?.status === 401) {
        setAuth(false);
      } else {
        toast.error("Could not load inquiries.");
      }
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (auth?.user) loadInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const logout = async () => {
    await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    setAuth(false);
    setInquiries([]);
    toast.success("Signed out.");
  };

  const [loginEmail, setLoginEmail] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const requestLink = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      await axios.post(`${API_URL}/auth/request-link`, { email: loginEmail }, { withCredentials: true });
      setLinkSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Could not send a sign-in link. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  if (auth === null) {
    return (
      <div data-testid="admin-loading" className="flex min-h-screen items-center justify-center bg-white pt-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#e63946]" />
      </div>
    );
  }

  if (!auth) {
    return (
      <div data-testid="admin-signin" className="flex min-h-screen items-center bg-white pt-20">
        <div className="mx-auto w-full max-w-md px-5 py-24">
          <div className="text-center">
            <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946]">Team only</p>
            <h1 className="font-display mt-5 text-4xl font-extrabold tracking-tight text-[#111111]">
              Team sign-in
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-neutral-600">
              Enter your team email and we'll send you a one-time sign-in link.
            </p>
          </div>

          {linkSent ? (
            <div data-testid="link-sent" role="status" className="mt-10 border border-neutral-200 bg-[#f8f9fa] px-5 py-6 text-center">
              <Mail className="mx-auto h-5 w-5 text-[#e63946]" />
              <p className="mt-3 text-sm leading-relaxed text-[#111111]">
                If {loginEmail} is on the team list, a sign-in link is on its way. It works once and expires in 15 minutes.
              </p>
              <button
                type="button"
                onClick={() => setLinkSent(false)}
                className="mt-4 text-[12px] font-semibold uppercase tracking-[0.16em] text-neutral-500 underline-offset-4 hover:text-[#111111] hover:underline"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form data-testid="link-login-form" onSubmit={requestLink} className="mt-10 space-y-5">
              <div>
                <label htmlFor="login-email" className="font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  Email
                </label>
                <input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  autoComplete="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-neutral-300 bg-white px-4 py-3 text-sm text-[#111111] placeholder:text-neutral-400 transition-colors duration-300 focus:border-[#111111] focus:outline-none"
                />
              </div>
              {loginError && (
                <p data-testid="login-error" role="alert" className="border border-[#e63946] bg-red-50 px-4 py-3 text-sm text-[#111111]">
                  {loginError}
                </p>
              )}
              <button
                type="submit"
                data-testid="link-login-button"
                disabled={loggingIn}
                className="inline-flex w-full items-center justify-center gap-2 bg-[#111111] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#e63946] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                Email me a sign-in link
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-white pt-28 md:pt-36">
      <div className="mx-auto max-w-5xl px-5 pb-24 md:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946]">Team inbox</p>
              <h1 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-[#111111] sm:text-4xl">
                Website inquiries
              </h1>
              <p className="mt-2 text-sm text-neutral-500">
                Signed in as {auth.user.name || auth.user.email}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="admin-refresh-button"
                onClick={loadInquiries}
                disabled={loadingList}
                className="inline-flex items-center gap-2 border border-neutral-300 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#111111] transition-colors duration-300 hover:border-[#111111] disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loadingList ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                type="button"
                data-testid="admin-logout-button"
                onClick={logout}
                className="inline-flex items-center gap-2 bg-[#111111] px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-[#e63946]"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        </Reveal>

        {forbidden && (
          <div data-testid="admin-forbidden" className="mt-10 border border-[#e63946] bg-red-50 p-6">
            <p className="text-sm font-semibold text-[#111111]">
              This account isn't on the team access list.
            </p>
            <p className="mt-1 text-sm text-neutral-600">
              Sign out and use an authorized team account to view inquiries.
            </p>
          </div>
        )}

        {!forbidden && (
          <div data-testid="admin-tabs" className="mt-10 flex gap-1 border-b border-neutral-200">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                aria-selected={tab === t.id}
                className={`-mb-px border-b-2 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${tab === t.id ? "border-[#e63946] text-[#111111]" : "border-transparent text-neutral-500 hover:text-[#111111]"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {!forbidden && tab === "quotes" && <div className="mt-8"><AdminQuotes /></div>}
        {!forbidden && tab === "payments" && <div className="mt-8"><AdminPayments /></div>}

        {!forbidden && tab === "inquiries" && (
          <div data-testid="inquiries-list" className="mt-6">            {inquiries.length === 0 && !loadingList ? (
              <div className="flex flex-col items-center gap-4 border border-dashed border-neutral-300 py-20 text-neutral-400">
                <Inbox className="h-8 w-8" />
                <p className="text-sm">No inquiries yet.</p>
              </div>
            ) : (
              inquiries.map((q, i) => (
                <Reveal key={q.id} delay={0.03 * i}>
                  <article data-testid={`inquiry-item-${i}`} className="border-t border-neutral-200 py-6">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <h2 className="font-display text-lg font-bold tracking-tight text-[#111111]">{q.name}</h2>
                      <time className="text-xs uppercase tracking-[0.15em] text-neutral-400">
                        {new Date(q.created_at).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </time>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-neutral-600">
                      <a href={`mailto:${q.email}`} className="font-medium text-[#111111] underline decoration-neutral-300 underline-offset-4 transition-colors duration-300 hover:text-[#e63946]">
                        {q.email}
                      </a>
                      {q.phone && <span>{q.phone}</span>}
                      <span className="bg-[#f8f9fa] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-600">
                        {q.interest}
                      </span>
                      {q.track && (
                        <span className="bg-[#f8f9fa] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-600">
                          {q.track}
                        </span>
                      )}
                      {q.preferred_contact && (
                        <span className="text-xs text-neutral-500">Prefers {q.preferred_contact.toLowerCase()}</span>
                      )}
                    </div>
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-neutral-700">{q.message}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        data-testid={`draft-reply-button-${i}`}
                        onClick={() => draftReply(q)}
                        disabled={drafts[q.id]?.loading}
                        className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#111111] transition-colors duration-300 hover:border-[#111111] disabled:opacity-50"
                      >
                        {drafts[q.id]?.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-[#e63946]" />}
                        Draft reply
                      </button>
                      {drafts[q.id]?.text && (
                        <a
                          data-testid={`draft-mailto-${i}`}
                          href={`mailto:${q.email}?subject=${encodeURIComponent(`Re: Your ABS LLC inquiry — ${q.interest}`)}&body=${encodeURIComponent(drafts[q.id].text)}`}
                          className="inline-flex items-center gap-2 bg-[#111111] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-[#e63946]"
                        >
                          <Mail className="h-3.5 w-3.5" />
                          Open in email
                        </a>
                      )}
                    </div>
                    {drafts[q.id] && (
                      <div data-testid={`draft-reply-${i}`} className="mt-4 whitespace-pre-wrap border border-neutral-200 bg-[#f8f9fa] p-5 text-sm leading-relaxed text-neutral-700">
                        {drafts[q.id].text || "Drafting…"}
                      </div>
                    )}
                  </article>
                </Reveal>
              ))
            )}
          </div>
        )}

        {!forbidden && tab === "inquiries" && <AdminRecapForm />}
      </div>
    </div>
  );
}
